import { createRouter, createWebHistory } from 'vue-router'
import {
  captureInvitationToken,
  isPreRegAccepted,
  setPreRegReturnRoute,
  clearPreRegReturnRoute,
  renewPreReg,
} from '../utils/preRegistration'

// Invited visitor links open the form directly, so these steps carry the same
// pre-registration gate as the public wizard.
//
// The media-guest steps deliberately stay out of this list. They accept the same
// invitation query (/registration/media-guest/step1?tyt=&tk=) but are a public
// form as well, so the gate must not intercept them; each media step reads the
// query itself via getMediaInviteQuery() in src/utils/preRegistration.js and
// carries it to the next step.
const INVITED_STEP_ROUTES = ['RegistrationStep1', 'RegistrationStep2', 'RegistrationStep3'];

// Cookie written by invited Step 1 once the visitor has passed the gate. Steps 2
// and 3 only continue an already gated registration, so it is what tells them
// apart from a cold deep link.
const INVITED_SESSION_COOKIE = '_AlUlaAt';

// SEO content for each page
export const seoContent = {
  en: {
    title: 'AlUla Conference on Emerging Market Economies (ACEME) | Official Website',
    description: 'The AlUla Conference on Emerging Market Economies (EME) is a high-level annual economic conference organized by the Saudi Ministry of Finance and the International Monetary Fund, bringing together finance ministers, central bank governors, policymakers, global investors, and institutional leaders to align economic policies amid global trade and monetary challenges.',
    keywords: 'AlUla Conference on Emerging Market Economies, AlUla ACEME, Annual Economic Conference, Finance Ministers, Central Bank Governors, Economic Policy Alignment, Emerging Markets, International Monetary Fund, Saudi Ministry of Finance'
  },
  ar: {
    title: 'مؤتمر العلا لاقتصادات الأسواق الناشئة | الموقع الرسمي',
    description: 'يُعد مؤتمر العلا لاقتصادات الأسواق الناشئة مؤتمرًا اقتصاديًا سنويًا رفيع المستوى، بتنظيم وزارة المالية وصندوق النقد الدولي، يجمع وزراء المالية ومحافظي البنوك المركزية وصنّاع السياسات وقادة المؤسسات الدولية لمناقشة مواءمة السياسات الاقتصادية في ظل تحديات التجارة العالمية والتحولات النقدية.',
    keywords: 'مؤتمر العلا لاقتصادات الأسواق الناشئة، AlUla ACEME، مؤتمر اقتصادي سنوي، وزراء المالية، محافظو البنوك المركزية، السياسات الاقتصادية، الأسواق الناشئة، صندوق النقد الدولي، وزارة المالية السعودية، مؤتمر العلا الاقتصادي'
  }
};

// Function to update meta tags based on locale
export const updateMetaTags = (locale = 'en', seo = seoContent[locale]) => {
  if (!seo) return;
  
  // Update title
  document.title = seo.title;
  
  // Update description meta tag
  let descMeta = document.querySelector("meta[name='description']");
  if (!descMeta) {
    descMeta = document.createElement('meta');
    descMeta.name = 'description';
    document.head.appendChild(descMeta);
  }
  descMeta.content = seo.description;
  
  // Update keywords meta tag
  let keywordsMeta = document.querySelector("meta[name='keywords']");
  if (!keywordsMeta) {
    keywordsMeta = document.createElement('meta');
    keywordsMeta.name = 'keywords';
    document.head.appendChild(keywordsMeta);
  }
  keywordsMeta.content = seo.keywords;
  
  // Update OG tags
  let ogTitle = document.querySelector("meta[property='og:title']");
  if (!ogTitle) {
    ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    document.head.appendChild(ogTitle);
  }
  ogTitle.content = seo.title;
  
  let ogDesc = document.querySelector("meta[property='og:description']");
  if (!ogDesc) {
    ogDesc = document.createElement('meta');
    ogDesc.setAttribute('property', 'og:description');
    document.head.appendChild(ogDesc);
  }
  ogDesc.content = seo.description;
};

const routes = [
  {
    path: '/side-tour',
    meta: { layout: "RegLayout" },
    name: 'SideTour',
    component: () => import('../views/Registration/SideTour.vue')
  },
  {
    path: '/round-table/:type',
    meta: { layout: "RegLayout" },
    name: 'RoundTable',
    component: () => import('../views/Registration/RoundTable.vue')
  },
  {
    path: '/gala-dinner/:type',
    meta: { layout: "RegLayout" },
    name: 'GalaDinner',
    component: () => import('../views/Registration/GalaDinner.vue')
  },

  {
    path: '/room-booking/:type',
    meta: { layout: "RegLayout" },
    name: 'RoomBooking',
    component: () => import('../views/Registration/RoomBooking.vue')
  },
  {
    path: '/room-booking-scan',
    name: 'RoomBookingMiddleware',
    component: () => import('../views/Registration/RoomBookingMiddleware.vue')
  },
  {
    path: '/questions-answers',
    meta: { layout: "webLayout" },
    name: 'QuestionAnswers',
    component: () => import('../views/Registration/QuestionAnswers.vue')
  },
  {
    path: '/registration',
    meta: { layout: "RegLayout" },
    children: [
      {
        path: 'final-step',
        name: 'FinalStep',
        // Thank-you page shared by every registration form.
        meta: { mobileHero: true },
        component: () => import('../views/Registration/ThankYou.vue')
      },
      {
        path: 'media-guest/step1',
        name: 'MediaStep1',
        // Same mobile hero as the public registration steps.
        meta: { mobileHero: true },
        component: () => import('../views/Registration/Media/MediaStep1.vue')
      },
      {
        path: 'media-guest/step2',
        name: 'MediaStep2',
        meta: { mobileHero: true },
        component: () => import('../views/Registration/Media/MediaStep2.vue')
      },
      {
        path: 'media-guest/step3',
        name: 'MediaStep3',
        meta: { mobileHero: true },
        component: () => import('../views/Registration/Media/MediaStep3.vue')
      },
      {
        path: 'visitor/step1',
        name: 'RegistrationStep1',
        // Same mobile hero as the public registration steps.
        meta: { mobileHero: true },
        component: () => import('../views/Registration/AllTypes/Step1.vue')
      },
      {
        path: 'visitor/step2',
        name: 'RegistrationStep2',
        meta: { mobileHero: true },
        component: () => import('../views/Registration/AllTypes/Step2.vue')
      },
      {
        path: 'visitor/step3',
        name: 'RegistrationStep3',
        meta: { mobileHero: true },
        component: () => import('../views/Registration/AllTypes/Step3.vue')
      },
      {
        path: 'organizer',
        name: 'inDoorRegistration',
        meta: { mobileHero: true },
        component: () => import('../views/Registration/inDoorRegistration/Step1.vue')
      },
      // Pre-registration gate: every public registration starts here.
      {
        path: 'public/intro',
        name: 'RegistrationPublicIntro',
        meta: { mobileHero: true, mobileFooter: true },
        component: () => import('../views/Registration/Public/PreRegistration.vue')
      },
      {
        path: 'public/privacy-notice',
        name: 'RegistrationPublicPrivacyNotice',
        meta: { mobileHero: true },
        component: () => import('../views/Registration/Public/PrivacyNotice.vue')
      },
      {
        path: 'public/step1',
        name: 'RegistrationPublicStep1',
        // Same mobile hero as the intro / privacy notice pages.
        meta: { mobileHero: true },
        component: () => import('../views/Registration/Public/Step1.vue')
      },
      {
        path: 'public/step2',
        name: 'RegistrationPublicStep2',
        meta: { mobileHero: true },
        component: () => import('../views/Registration/Public/Step2.vue')
      },
      {
        path: 'public/step3',
        name: 'RegistrationPublicStep3',
        meta: { mobileHero: true },
        component: () => import('../views/Registration/Public/Step3.vue')
      },
    ]
  },

  // PROFILE ROUTES
  {
    path: '/profile',
    meta: { layout: "ProfileLayout" },
    children: [
      {
        path: 'login',
        name: 'ProfileLogin',
        component: () => import('../views/Profile/Login.vue')
      },
      {
        path: 'otp',
        name: 'ProfileOTP',
        component: () => import('../views/Profile/OTP.vue')
      },
      {
        path: 'dashboard',
        name: 'ProfileDashboard',
        meta: { requiresProfileAuth: true },
        component: () => import('../views/Profile/Dashboard.vue')
      },
      {
        path: 'badge',
        name: 'ProfileBadge',
        meta: { requiresProfileAuth: true },
        component: () => import('../views/Profile/Badge.vue')
      },
      {
        path: 'hotel',
        name: 'ProfileHotel',
        meta: { requiresProfileAuth: true },
        component: () => import('../views/Profile/Hotel.vue')
      },
      {
        path: 'aviation',
        name: 'ProfileAviation',
        meta: { requiresProfileAuth: true },
        component: () => import('../views/Profile/Aviation.vue')
      },
    ]
  },
  {
    path: '/badge',
    name: 'ProfileBadgeNoAuth',
    component: () => import('../views/Profile/BadgeNoAuth.vue')
  },
  // ADMIN ROUTES
  {
    path: '/dashboard',
    meta: { layout: "DashboardLayout" },
    children: [
      {
        path: 'login',
        name: 'AdminLogin',
        component: () => import('../views/Admin/Login.vue')
      },
      {
        path: 'otp',
        name: 'AdminOTP',
        component: () => import('../views/Admin/OTP.vue')
      },
      {
        path: 'index',
        name: 'AdminDashboard',
        meta: { requiresAdminAuth: true },
        component: () => import('../views/Admin/Dashboard.vue')
      },
    ]
  },
  // AGENT DASHBAORD ROUTES
  // ADMIN ROUTES
  {
    path: '/agent',
    meta: { layout: "AgentLayout" },
    children: [
      {
        path: 'login',
        name: 'AgentLogin',
        component: () => import('../views/Agent/Login.vue')
      },
      {
        path: 'otp',
        name: 'AgentOTP',
        component: () => import('../views/Agent/OTP.vue')
      },
      {
        path: 'print',
        name: 'AgentPrint',
        meta: { requiresAgentAuth: true },
        component: () => import('../views/Agent/Print.vue')
      },
      {
        path: 'checkin',
        name: 'AgentCheckin',
        meta: { requiresAgentAuth: true },
        component: () => import('../views/Agent/CheckinView.vue')
      },
    ]
  },
  // AGENT DASHBAORD ROUTES
  // ADMIN ROUTES
  {
    path: '/room-agent',
    meta: { layout: "RoomAgentLayout" },
    children: [
      {
        path: 'login',
        name: 'RoomAgentLogin',
        component: () => import('../views/RoomAgent/Login.vue')
      },
      {
        path: 'otp',
        name: 'RoomAgentOTP',
        component: () => import('../views/RoomAgent/OTP.vue')
      },
      {
        path: 'checkin',
        name: 'RoomAgentCheckin',
        meta: { requiresRoomAgentAuth: true },
        component: () => import('../views/RoomAgent/CheckinView.vue')
      },
    ]
  },
  //website
  {
    path: '/',
    meta: { layout: "WebLayout" },
    children: [
      {
        path: '',
        name: 'homepage',
        component: () => import('../views/website/homepage.vue')
      },
      {
        path: 'about-us',
        name: 'aboutpage',
        component: () => import('../views/website/aboutpage.vue')
      },
      {
        path: 'contact-us',
        name: 'contactpage',
        component: () => import('../views/website/contactpage.vue')
      },
      {
        path: 'faq',
        name: 'faqpage',
        component: () => import('../views/website/faqpage.vue')
      },
      {
        path: 'conference-agenda',
        name: 'agendapage',
        component: () => import('../views/website/agendapage.vue')
      },
      {
        path: 'research',
        name: 'researchpage',
        component: () => import('../views/website/researchpage.vue')
      },
      {
        path: 'news',
        name: 'newspage',
        component: () => import('../views/website/newspage.vue')
      },
      {
        path: 'news/:id',
        name: 'newspageIndex',
        component: () => import('../views/website/newspageIndex.vue')
      },
      {
        path: 'media-center',
        name: 'mediapage',
        component: () => import('../views/website/mediapage.vue')
      },
      {
        path: 'speakers',
        name: 'speakerpage',
        component: () => import('../views/website/speakerpage.vue')
      },
      {
        path: 'participants',
        name: 'participantpage',
        component: () => import('../views/website/participantpage.vue')
      },
      {
        path: 'highlights',
        name: 'highlightpage',
        component: () => import('../views/website/highlightpage.vue')
      },
    ]
  },
  // end of website
  
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue')
  },
  {
    path: '/registration/not-found',
    name: 'RegNotFound',
    component: () => import('../views/RegNotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from) {    
      return { top: 0, behavior: 'smooth' }
  }
})
router.beforeEach((to, from, next) => {
  // Get current locale from localStorage (set by i18n)
  const locale = localStorage.getItem('locale') || 'en';
  
  // Update meta tags for homepage
  if (to.name === 'homepage') {
    updateMetaTags(locale);
  }

  // Invitation links (companion invitations) are /registration/public/step1?tk=<token>.
  // The pre-registration gate redirects through the intro and privacy pages without the
  // query string, so the token is stored in the registration session cookie first; the
  // wizard then resumes the invited record instead of starting a new registration.
  if (to.query && to.query.tk && String(to.name || '').startsWith('RegistrationPublic')) {
    captureInvitationToken(to.query.tk);
    // A public / companion invitation is the latest intent: drop any invited step
    // parked by an earlier visitor link so the privacy page cannot replay it and
    // push the companion into the invited form.
    clearPreRegReturnRoute();
  }

  // Invited links (/registration/visitor/step1|2|3?tk=&tyt=) open the form directly,
  // so the visitor has to pass the same pre-registration gate as the public form.
  // The requested step is parked first, because the introduction and privacy pages
  // drop the query string; the privacy page replays it once the consent is given.
  if (INVITED_STEP_ROUTES.includes(to.name)) {
    if (isPreRegAccepted()) {
      // Consent on file: nothing to replay, and keep it alive so a wizard that is
      // still in progress is not cut short when the cookie reaches its expiry.
      clearPreRegReturnRoute();
      renewPreReg();
    } else if (to.name !== 'RegistrationStep1' && getCookie(INVITED_SESSION_COOKIE)) {
      // Steps 2 and 3 only continue a registration whose Step 1 - gated on its own -
      // already stored the session cookie. An expired consent must not throw the
      // visitor back to the introduction page and discard the work in progress.
      clearPreRegReturnRoute();
    } else {
      setPreRegReturnRoute({ name: to.name, query: to.query });
      next({ name: 'RegistrationPublicIntro' });
      return;
    }
  }
  
  const requiresProfileAuth = to.matched.some(record => record.meta.requiresProfileAuth);
  const isProfileAuthenticated = getCookie('_al_Pu_jwt') ? true : false;
  const requiresAdminAuth = to.matched.some(record => record.meta.requiresAdminAuth);
  const requiresAgentAuth = to.matched.some(record => record.meta.requiresAgentAuth);
  const isAdminAuthenticated = getCookie('_al_Adu') ? true : false;
  const isAgentAuthenticated = getCookie('_al_Agtu') ? true : false;

  const requiresRoomAgentAuth = to.matched.some(record => record.meta.requiresRoomAgentAuth);
  const isRoomAgentAuthenticated = getCookie('_al_RAgtu') ? true : false;

  if (requiresRoomAgentAuth && !isRoomAgentAuthenticated) {
    next({ name: 'RoomAgentLogin' });
  } 
  else if (requiresAgentAuth && !isAgentAuthenticated) {
    next({ name: 'AgentLogin' });
  } 
  else if (requiresProfileAuth && !isProfileAuthenticated) {
    next({ name: 'ProfileLogin' });
  } 
  else if (requiresAdminAuth && !isAdminAuthenticated) {
    next({ name: 'AdminLogin' });
  }
  else {
    next();
  }
});

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

export default router
